

import { useState, useEffect, useRef, useCallback } from 'react'
import { apiFetch } from '@/lib/api'
import { useShow } from '@/contexts/ShowContext'
import { useEpisode } from '@/contexts/EpisodeContext'
import { UserRole } from '@/contexts/UserContext'
import { useParams } from 'react-router-dom'
import { LuLoaderCircle } from 'react-icons/lu'
import Button from '@/components/ui/Button'
import CommentMentionsInput from '@/components/comments/CommentMentionsInput'
import CommentGroup from '@/components/comments/CommentGroup'
import { useEditor } from './store'

export function formatPlayheadTime(seconds) {
    const m = Math.floor(seconds / 60)
    const s = Math.floor(seconds % 60)
    const f = Math.floor((seconds % 1) * 30)
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}:${String(f).padStart(2, '0')}`
}

export function useTimelineComments(timelineId) {
    const { show } = useShow()
    const [comments, setComments] = useState([])
    const [loading, setLoading] = useState(true)

    const fetchComments = useCallback(async () => {
        if (!show?._id || !timelineId) {
            setComments([])
            setLoading(false)
            return
        }
        try {
            const response = await apiFetch(`/api/shows/${show._id}/comments?uid=${timelineId}&sort=timestamp`)
            if (!response.ok) throw new Error('Failed to fetch comments')
            const data = await response.json()
            setComments(data)
        } catch (error) {
            console.error('Error fetching comments:', error)
        } finally {
            setLoading(false)
        }
    }, [show?._id, timelineId])

    useEffect(() => {
        setLoading(true)
        fetchComments()
    }, [fetchComments])

    const addComment = useCallback((comment) => {
        setComments(prev => [...prev, comment])
    }, [])

    const updateComment = useCallback((updated) => {
        setComments(prev => prev.map(c => c._id === updated._id ? { ...c, ...updated } : c))
    }, [])

    return { comments, loading, fetchComments, addComment, updateComment }
}

export default function TimelineComments({ timelineId, comments, loading, fetchComments, addComment, updateComment, highlightCommentId }) {
    const { show } = useShow()
    const { episode } = useEpisode()
    const { segmentId } = useParams()
    const playhead = useEditor((s) => s.playhead)
    const setPlayhead = useEditor((s) => s.setPlayhead)

    const [commentText, setCommentText] = useState('')
    const commentInputRef = useRef(null)
    const highlightRef = useRef(null)

    useEffect(() => {
        if (highlightCommentId && highlightRef.current) {
            highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
    }, [highlightCommentId])

    const handleCommentSubmit = async () => {
        if (!commentText.trim()) return
        try {
            const response = await apiFetch(`/api/shows/${show._id}/comments`, {
                method: 'POST',
                body: JSON.stringify({
                    uid: timelineId,
                    text: commentText,
                    element_type: 'timeline',
                    episode: episode?._id,
                    segment: segmentId,
                    timestamp: playhead
                })
            })
            if (!response.ok) throw new Error('Failed to submit comment')
            const data = await response.json()
            addComment(data)
            setCommentText('')
        } catch (error) {
            console.error('Error submitting comment:', error)
        }
    }

    const handleCommentKeyDown = (e) => {
        if (e.key === 'Enter' && e.ctrlKey) {
            e.preventDefault()
            handleCommentSubmit()
        } else if (e.key === 'Escape') {
            setCommentText('')
        }
    }

    const handleCommentClick = (comment) => {
        if (comment.timestamp != null) {
            setPlayhead(comment.timestamp)
        }
    }

    return (
        <div className='w-72 flex flex-col border-l border-l-secondary/60 bg-background/50 shrink-0'>
            <div className='flex items-center justify-between px-3 h-10 border-b border-b-secondary/60'>
                <span className='text-xs font-medium text-secondary-foreground'>Comments</span>
                {comments.length > 0 && <span className='text-[10px] text-secondary-foreground/50 bg-secondary rounded-full px-1.5 py-0.5 min-w-[20px] text-center'>{comments.length}</span>}
            </div>

            <div className='flex-1 overflow-y-auto px-2 py-2'>
                {loading ? (
                    <div className='flex items-center justify-center py-8'>
                        <LuLoaderCircle className='w-4 h-4 animate-spin text-secondary-foreground/50' />
                    </div>
                ) : comments.length === 0 ? (
                    <div className='flex items-center justify-center py-8'>
                        <span className='text-xs text-secondary-foreground/40'>No comments yet</span>
                    </div>
                ) : (
                    <div className='flex flex-col gap-2'>
                        {comments.map((comment) => (
                            <div key={comment._id} ref={highlightCommentId === comment._id ? highlightRef : undefined}>
                            <CommentGroup
                                data={comment}
                                onDelete={fetchComments}
                                onUpdate={updateComment}
                                onClick={() => handleCommentClick(comment)}
                                highlightCommentId={highlightCommentId}
                                elementName={comment.timestamp != null ? formatPlayheadTime(comment.timestamp) : undefined}
                            />
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <UserRole role='commentator'>
                <div className='p-2'>
                    <div className='flex flex-col gap-1'>
                        <span className='text-[10px] text-secondary-foreground/50 px-1'>
                            Commenting at: <span className='text-primary/70'>{formatPlayheadTime(playhead)}</span>
                        </span>
                        <CommentMentionsInput
                            value={commentText}
                            onChange={setCommentText}
                            onKeyDown={handleCommentKeyDown}
                            placeholder='Add a comment...'
                            inputRef={commentInputRef}
                        />
                        <div className='flex items-center justify-end gap-1'>
                            <Button type='confirm' size='xs' onClick={handleCommentSubmit} disabled={!commentText.trim()}>
                                Comment
                            </Button>
                        </div>
                    </div>
                </div>
            </UserRole>
        </div>
    )
}
